import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Briefcase, Building2, ArrowRight } from 'lucide-react';
const resources = [{
  title: 'Salary Guides',
  description: 'Compensation data across specialties and regions.',
  icon: BarChart3,
  href: '#'
}, {
  title: 'All About Locums',
  description: 'Everything you need to know about locum tenens work.',
  icon: Briefcase,
  href: '#'
}, {
  title: 'Facility Resources',
  description: 'Tools and guides for healthcare facilities and staffing.',
  icon: Building2,
  href: '#'
}];
// Premium easing curves
const easeOut = [0.16, 1, 0.3, 1]; // Apple-style smooth deceleration
// Orchestrated container animation
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.3
    }
  }
};
// Refined item animation - fade only
const itemVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: easeOut
    }
  }
};
// Left content animation - fade only
const contentVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: easeOut
    }
  }
};
export function BlogHeroVariantA() {
  return <section className="relative pt-36 pb-16 overflow-hidden min-h-[540px]" data-id="element-108">
      {/* Warm gradient background */}
      <div className="absolute inset-0" style={{
      background: 'linear-gradient(135deg, #374586 0%, #3f5899 40%, #5a6fad 100%)'
    }} data-id="element-109" />

      {/* Subtle dot grid pattern */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" data-id="element-110">
        <div className="absolute inset-0 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:24px_24px]" data-id="element-111" />
      </div>

      {/* Skewed parallelogram shape - full height, no rounded corners */}
      <div className="absolute right-[-5%] w-[55%]" style={{
      top: 0,
      bottom: 0,
      clipPath: 'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%)',
      background: 'linear-gradient(rgba(43, 43, 112, 0.125) 0%, rgba(43, 43, 112, 0.25) 50%, rgba(43, 43, 112, 0.4) 100%)'
    }} data-id="element-112" />

      {/* Soft accent blob */}
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#b9383e]/[0.05] translate-y-1/3 -translate-x-1/4" data-id="element-113" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-114">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start" data-id="element-115">
          {/* Header Content - 60% (3 of 5 columns) */}
          <motion.div initial="hidden" animate="visible" variants={contentVariants} className="lg:col-span-3" data-id="element-116">
            <span className="inline-block font-medium mb-6 tracking-wide uppercase" style={{
            fontFamily: "'Poppins', sans-serif",
            padding: '0.4rem 0.85rem',
            borderRadius: '9999px',
            backgroundColor: 'rgba(43, 43, 112, 0.5)',
            border: '1px solid #5a6fad',
            color: 'rgb(219, 234, 254)',
            fontSize: '0.7875rem',
            fontWeight: 500,
            backdropFilter: 'blur(4px)'
          }} data-id="element-117">
              All Star Blog
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-5 leading-[1.33] tracking-tight" data-id="element-118">
              Insights for the <br data-id="element-119" />
              modern provider
            </h1>
            <p className="text-lg text-white max-w-xl leading-relaxed mb-8 font-light" style={{
            fontFamily: "'Poppins', sans-serif"
          }} data-id="element-120">
              Expert guidance, industry trends, and career resources designed to
              help you navigate and thrive in your healthcare journey.
            </p>

            {/* Subscribe Form - pill enveloping button */}
            <div className="relative max-w-md" data-id="element-121">
              <input type="email" placeholder="Get alerts straight to your inbox" className="w-full pl-5 pr-44 py-4 bg-white rounded-full text-slate-800 placeholder-slate-600 text-base font-light focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg" style={{
              fontFamily: "'Poppins', sans-serif"
            }} data-id="element-122" />
              <button className="absolute top-1/2 -translate-y-1/2 px-8 py-3 bg-[#b9383e] hover:bg-[#780935] text-white rounded-full font-medium transition-colors whitespace-nowrap" style={{
              fontFamily: "'Poppins', sans-serif",
              right: '4px'
            }} data-id="element-123">
                Subscribe
              </button>
            </div>
          </motion.div>

          {/* Resource Links - 40% (2 of 5 columns) */}
          <motion.div className="lg:col-span-2 flex flex-col gap-4" initial="hidden" animate="visible" variants={containerVariants} data-id="element-124">
            {resources.map(item => <motion.a href={item.href} key={item.title} variants={itemVariants} className="group relative flex items-center p-5 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 hover:bg-white/15 transition-all duration-300 overflow-hidden" data-id="element-125">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center mr-5 group-hover:scale-105 transition-transform duration-300 bg-[#DB9744]" data-id="element-126">
                  <item.icon className="w-5 h-5 text-white" data-id="element-127" />
                </div>

                <div className="flex-1 min-w-0 mr-3" data-id="element-128">
                  <h3 className="text-xl font-semibold text-white mb-1 group-hover:text-white/90 transition-colors" style={{
                fontFamily: "'Poppins', sans-serif"
              }} data-id="element-129">
                    {item.title}
                  </h3>
                  <p className="text-sm text-white/70 font-light" style={{
                fontFamily: "'Poppins', sans-serif"
              }} data-id="element-130">
                    {item.description}
                  </p>
                </div>

                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" data-id="element-131">
                  <ArrowRight className="w-4 h-4 text-white" data-id="element-132" />
                </div>
              </motion.a>)}
          </motion.div>
        </div>
      </div>
    </section>;
}